export type RotationDegrees = 0 | 90 | 180 | 270

// Landscape canvas used to build the vertical edge-density projection profiles.
const RENDER_WIDTH = 256
const RENDER_HEIGHT = 128

// Fraction of each side excluded from the profile. Photographed "Image" scans carry a
// dark frame around the check whose hard edges otherwise dominate the profile and swamp
// the printed content; trimming the outer border removes it while keeping the content.
const EDGE_MARGIN = 0.08

/**
 * Detect the rotation needed for `targetSrc` to match the orientation of `referenceSrc`.
 *
 * Uses two additive signals, both operating on vertical *edge-density projection profiles*
 * (summed gradient magnitude per row), to choose between the two orientation candidates:
 *
 * 1. **Reference correlation** — correlate each candidate's z-scored profile against the
 *    TIFF's profile. Works well when the TIFF's edge density is driven by its content
 *    (dense MICR at bottom, sparse header at top). Unreliable when the TIFF carries a
 *    decorative background (watermark/guilloché): its edges dominate the profile's variance
 *    and bury the content structure, so after normalization the profile reflects the pattern
 *    rather than the layout and correlation with a clean target is near-random.
 *
 * 2. **Structural bottom-heavy bias** — every check has the MICR line at the bottom, so
 *    the correctly-oriented candidate's profile should have higher z-scores in the lower
 *    half. This signal works entirely from the target image and is unaffected by how the
 *    TIFF looks. It compensates for the cases where signal 1 is weak.
 *
 * Profiles are robust to horizontal misalignment, background color, contrast and solid fills.
 *
 * Used to align "Image" check scans against their corresponding "TIFF" version,
 * which is reliably oriented horizontally and right-side-up.
 */
export async function detectRotationFromReference(targetSrc: string, referenceSrc: string): Promise<RotationDegrees> {
	const [target, reference] = await Promise.all([loadImageElement(targetSrc), loadImageElement(referenceSrc)])

	// Aspect ratio decides whether a sideways rotation is needed. The two viable
	// rotations always differ by 180°, which just flips the check top-to-bottom.
	const targetIsLandscape = target.naturalWidth >= target.naturalHeight
	const referenceIsLandscape = reference.naturalWidth >= reference.naturalHeight
	const [uprightRotation, flippedRotation]: RotationDegrees[] =
		targetIsLandscape !== referenceIsLandscape ? [90, 270] : [0, 180]

	// Render both candidate rotations independently and correlate each against the
	// reference. A 180° flip reverses the row profile in theory, but on framed photos the
	// hard border edges sample differently per rotation, so reusing one reversed profile
	// is unreliable — two renders on a 256×128 canvas are cheap and correct.
	const referenceProfile = normalizeProfile(rowEdgeProfile(reference, 0))
	const uprightProfile = normalizeProfile(rowEdgeProfile(target, uprightRotation))
	const flippedProfile = normalizeProfile(rowEdgeProfile(target, flippedRotation))

	let uprightScore = 0
	let flippedScore = 0
	const n = referenceProfile.length
	const mid = Math.floor(n / 2)
	for (let i = 0; i < n; i++) {
		// Signal 1: reference correlation — how well the candidate's layout matches the TIFF.
		// Weak when a decorative background dominates the TIFF profile and buries its content.
		uprightScore += uprightProfile[i] * referenceProfile[i]
		flippedScore += flippedProfile[i] * referenceProfile[i]

		// Signal 2: structural bias — every check has the MICR line at the bottom, creating
		// the highest edge density in the lower rows. The correctly-oriented candidate's
		// normalized profile has higher z-scores toward the bottom, so weighting rows below
		// the midpoint positively and above it negatively identifies upright orientation
		// even when signal 1 is unreliable.
		const positionBias = i >= mid ? 1 : -1
		uprightScore += positionBias * uprightProfile[i]
		flippedScore += positionBias * flippedProfile[i]
	}

	return flippedScore > uprightScore ? flippedRotation : uprightRotation
}

function loadImageElement(src: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image()
		img.onload = () => resolve(img)
		img.onerror = () => reject(new Error(`Failed to load image: ${src.slice(0, 64)}...`))
		img.src = src
	})
}

/**
 * Render `img` rotated by `rotation`, scaled to fill a landscape canvas, and return the
 * per-row edge density: the summed magnitude of horizontal and vertical luminance
 * gradients across each row. Linear magnitude (rather than squared) keeps a few very
 * strong edges — e.g. a residual dark frame — from swamping the many moderate edges of
 * printed text, so the profile reflects the true content distribution: dense toward the
 * bottom (MICR line, signature), sparse at the top. The outer `EDGE_MARGIN` border is
 * excluded to drop the photo frame; solid regions (redaction bars) have near-zero
 * internal gradient and contribute nothing either way.
 */
function rowEdgeProfile(img: HTMLImageElement, rotation: RotationDegrees): Float64Array {
	const data = renderRotated(img, rotation, RENDER_WIDTH, RENDER_HEIGHT)
	const marginX = Math.floor(RENDER_WIDTH * EDGE_MARGIN)
	const marginY = Math.floor(RENDER_HEIGHT * EDGE_MARGIN)

	const profile = new Float64Array(RENDER_HEIGHT)
	// A gradient at (x, y) only needs the pixel to its left and the pixel above it,
	// so two row-sized buffers suffice instead of a full-image luminance array.
	let prevRow = new Float64Array(RENDER_WIDTH)
	let currRow = new Float64Array(RENDER_WIDTH)

	for (let y = 0; y < RENDER_HEIGHT; y++) {
		let edges = 0
		for (let x = 0; x < RENDER_WIDTH; x++) {
			const p = (y * RENDER_WIDTH + x) * 4
			currRow[x] = 0.299 * data[p] + 0.587 * data[p + 1] + 0.114 * data[p + 2]
			// Accumulate edges only inside the trimmed content region.
			if (y <= marginY || y >= RENDER_HEIGHT - marginY) continue
			if (x <= marginX || x >= RENDER_WIDTH - marginX) continue
			edges += Math.abs(currRow[x] - currRow[x - 1]) // horizontal gradient
			edges += Math.abs(currRow[x] - prevRow[x]) // vertical gradient
		}
		profile[y] = edges
		// Reuse the old prevRow buffer as next iteration's currRow; it gets fully overwritten.
		const swap = prevRow
		prevRow = currRow
		currRow = swap
	}
	return profile
}

/**
 * Render `img` rotated by `rotation` degrees, scaled to fully cover the
 * `targetWidth` x `targetHeight` canvas, and return its raw RGBA pixel data.
 */
function renderRotated(
	img: HTMLImageElement,
	rotation: RotationDegrees,
	targetWidth: number,
	targetHeight: number
): Uint8ClampedArray {
	const canvas = document.createElement("canvas")
	canvas.width = targetWidth
	canvas.height = targetHeight
	const ctx = canvas.getContext("2d")
	if (!ctx) return new Uint8ClampedArray(targetWidth * targetHeight * 4)

	const isSidewaysRotation = rotation === 90 || rotation === 270
	// After rotating around the canvas center, the image is stretched to fill the
	// pre-rotation dimensions, so for 90/270 we swap width and height when drawing.
	const drawWidth = isSidewaysRotation ? targetHeight : targetWidth
	const drawHeight = isSidewaysRotation ? targetWidth : targetHeight

	ctx.fillStyle = "#ffffff"
	ctx.fillRect(0, 0, targetWidth, targetHeight)

	ctx.save()
	ctx.translate(targetWidth / 2, targetHeight / 2)
	ctx.rotate((rotation * Math.PI) / 180)
	ctx.drawImage(img, -drawWidth / 2, -drawHeight / 2, drawWidth, drawHeight)
	ctx.restore()

	return ctx.getImageData(0, 0, targetWidth, targetHeight).data
}

function normalizeProfile(profile: Float64Array): Float64Array {
	const n = profile.length
	let mean = 0
	for (let i = 0; i < n; i++) mean += profile[i]
	mean /= n
	let variance = 0
	for (let i = 0; i < n; i++) variance += (profile[i] - mean) ** 2
	variance /= n
	const std = Math.sqrt(variance) || 1
	const normalized = new Float64Array(n)
	for (let i = 0; i < n; i++) normalized[i] = (profile[i] - mean) / std
	return normalized
}
